import {
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  PopoverOrigin,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import Checkbox from "./Checkbox";
import { SxProps } from "@mui/system";
import Radio from "./Radio";
import { LucideIcon } from "lucide-react";

export type Option = {
  label: string;
  labelElement?: React.ReactNode;
  value: string;
  defaultChecked?: boolean;
  // When checked, all other selections will be cleared.
  // If checked and another check is selected, it will be cleared.
  mutuallyExclusive?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
};

type Props = {
  options: Option[];
  initial?: string[];
  onChange: (s: Option[]) => void;
  displayValue?: string | React.ReactNode;
  disabled?: boolean;
  maxDropdownHeight?: string;
  sx?: SxProps;
  sxSelect?: SxProps;
  sxPaper?: SxProps;
  radio?: boolean;
  noDecoration?: boolean;
  hasIcon?: boolean;
  noOutline?: boolean;
  ActiveIcon?: LucideIcon;
  closeOnSelect?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
};

export default ({
  options,
  initial,
  onChange,
  displayValue,
  maxDropdownHeight,
  disabled = false,
  radio = false,
  noDecoration = false,
  hasIcon = true,
  sx,
  sxSelect,
  sxPaper,
  noOutline = false,
  ActiveIcon,
  closeOnSelect = false,
  anchorOrigin,
  transformOrigin,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<string[]>(
    initial || options.filter((s) => s.defaultChecked).map((s) => s.value),
  );
  const optionsMap = new Map(options.map((o) => [o.value, o]));
  const handleChange = (event: SelectChangeEvent<typeof values>) => {
    const {
      target: { value },
    } = event;
    const updatedValues = typeof value === "string" ? value.split(",") : value;
    const newEntries = updatedValues.filter((v) => values.indexOf(v) < 0);
    let newValues: string[];
    if (
      newEntries.length === 1 &&
      (radio || optionsMap.get(newEntries[0])?.mutuallyExclusive)
    ) {
      // Case where new value is an "all" case or radio is enabled.
      newValues = newEntries;
    } else if (newEntries.length > 0) {
      // Case where we want to uncheck "all"
      newValues = updatedValues.filter(
        (v) => !(radio || optionsMap.get(v)?.mutuallyExclusive),
      );
    } else {
      // Case where we are unchecking something or otherwise don't care.
      newValues = updatedValues;
    }
    setValues(newValues);
    onChange(
      newValues.map((v) => optionsMap.get(v)).filter((v) => !!v) as Option[],
    );
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const activeIcon = (isActive: boolean) => {
    if (noDecoration) {
      return <></>;
    } else if (radio) {
      return <Radio size="small" checked={isActive} />;
    } else if (ActiveIcon) {
      return (
        <ActiveIcon
          width={20}
          style={{
            marginRight: "8px",
            visibility: isActive ? "visible" : "hidden",
          }}
        />
      );
    } else {
      return <Checkbox checked={isActive} />;
    }
  };

  return (
    <FormControl sx={{ cursor: "pointer", ...sx }}>
      <Select
        disabled={disabled}
        value={values}
        multiple
        displayEmpty
        open={open}
        onClick={(e) => {
          // Check if the click is exactly on the element with the event listener
          if (e.target === e.currentTarget || radio || closeOnSelect) {
            setOpen(!open);
          }
        }}
        onClose={handleClose}
        onOpen={handleOpen}
        renderValue={(selected) => {
          let val =
            displayValue ||
            selected.map((s) => optionsMap.get(s)?.label || "").join(", ");
          return (
            <Typography component="span" variant="body">
              {val}
            </Typography>
          );
        }}
        label={displayValue}
        onChange={handleChange}
        sx={{
          cursor: "pointer",
          minHeight: noOutline ? "unset" : "48px",
          ".MuiTypography-root": noOutline
            ? {
                fontSize: "14px",
                fontWeight: "700",
                color: "#262626",
              }
            : {},
          fieldset: {
            borderRadius: "100px",
            border: noOutline
              ? "none !important"
              : "2px solid #EAEBEC !important",
            "&:hover": {
              border: noOutline
                ? "none !important"
                : "2px solid #EAEBEC !important",
            },
          },
          ".MuiInputBase-input": {
            marginTop: "0px",
          },
          ...sxSelect,
        }}
        MenuProps={{
          anchorOrigin,
          transformOrigin,
          PaperProps: {
            sx: {
              ...sxPaper,
              marginTop: "8px",
              borderRadius: "12px",
              border: "2px solid #EAEBEC",
              background: "#FFF",
              display: "flex",
              padding: "8px 20px",
              flexDirection: "column",
              alignItems: "flex-start",
              boxShadow: "none",
              maxHeight: maxDropdownHeight || undefined,
              ul: {
                padding: 0,
                margin: 0,
                li: {
                  padding: 0,
                  "&.Mui-selected, &:hover": {
                    backgroundColor: "transparent",
                  },
                  "&.Mui-selected, &:focus": {
                    backgroundColor: "transparent",
                  },
                  "&:last-child": {
                    marginBottom: "0px",
                  },
                },
              },
            },
          },
        }}
        IconComponent={() => {
          return hasIcon ? (
            <div
              style={{
                right: "20px",
                top: noOutline ? "3px" : "16px",
                pointerEvents: "none",
                position: "absolute",
              }}
            >
              <ChevronDownIcon
                height="16px"
                width="16px"
                strokeWidth="3px"
                color="#8E9598"
              />
            </div>
          ) : (
            <></>
          );
        }}
      >
        {options.map((o) => (
          <MenuItem
            key={o.value}
            value={o.value}
            disableRipple={true}
            disableTouchRipple={true}
            sx={{
              "&:hover": { backgroundColor: "transparent" },
              "&.Mui-selected:hover": { backgroundColor: "transparent" },
              display: "flex",
            }}
          >
            {activeIcon(values.indexOf(o.value) > -1)}
            {o.startIcon}
            {o.labelElement || (
              <ListItemText primary={o.label} sx={{ fontWeight: "700" }} />
            )}
            {o.endIcon}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
